import { apiClient } from './api'
import type { ApiResponse } from '@/types/common'

export interface UserFilters {
  role?: 'admin' | 'instructor' | 'affiliate' | 'student'
  search?: string
  limit?: number
  offset?: number
}

export interface CreateUserRequest {
  email: string
  password: string
  name: string
  role: 'admin' | 'instructor' | 'affiliate' | 'student'
  phone?: string
  // Affiliate-specific (required when role=affiliate)
  affiliateCode?: string
  commissionType?: 'percentage' | 'fixed'
  commissionValue?: number
}

export interface UpdateUserRequest {
  email?: string
  password?: string // Optional: if provided, will update password
  name?: string
  role?: 'admin' | 'instructor' | 'affiliate' | 'student'
  isActive?: boolean
}

export interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'instructor' | 'affiliate' | 'student'
  phone?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CheckCodeResponse {
  available: boolean
  code: string
}

export const userService = {
  getUsers: async (filters?: UserFilters): Promise<ApiResponse<User[]>> => {
    return apiClient.get<User[]>('/admin/users', { params: filters })
  },

  getUserById: async (id: string): Promise<ApiResponse<User>> => {
    return apiClient.get<User>(`/admin/users/${id}`)
  },

  createUser: async (data: CreateUserRequest): Promise<ApiResponse<User>> => {
    return apiClient.post<User>('/admin/users', data)
  },

  updateUser: async (id: string, data: UpdateUserRequest): Promise<ApiResponse<User>> => {
    return apiClient.put<User>(`/admin/users/${id}`, data)
  },

  deleteUser: async (id: string): Promise<ApiResponse<void>> => {
    return apiClient.delete<void>(`/admin/users/${id}`)
  },

  checkAffiliateCode: async (code: string): Promise<ApiResponse<CheckCodeResponse>> => {
    return apiClient.get<CheckCodeResponse>('/admin/users/check-affiliate-code', {
      params: { code },
    })
  },

  resetUserTOTP: async (userId: string): Promise<ApiResponse<void>> => {
    return apiClient.post<void>(`/admin/users/${userId}/totp/reset`)
  },
}

export default userService
